/* Documents → Advance Tracking: upload ITPS / EMS / CMS number lists, see balance, auto-issued to Hub orders by service. */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Barcode, Loader2, RefreshCw, Search, Upload } from "lucide-react";
import { API, getAuthHeader } from "@/App";

const SERVICES = ["ITPS", "EMS", "CMS"];
const ST = { free: "bg-emerald-50 text-emerald-700", used: "bg-sky-50 text-sky-700", void: "bg-slate-100 text-slate-500" };

const BalanceCard = ({ s, d, active, onPick, onUpload, busy }) => (
  <div className={`rounded-xl border bg-white p-4 ${active ? "ring-2 ring-teal-500" : ""}`} data-testid={`adv-card-${s}`}>
    <button type="button" onClick={onPick} className="flex w-full items-center gap-2 text-left">
      <Barcode className="h-5 w-5 text-teal-700" /><b className="text-base">{s}</b>
      {d.low && <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700" data-testid={`adv-low-${s}`}><AlertTriangle className="h-3 w-3" /> Low</span>}
    </button>
    <p className={`mt-2 text-3xl font-bold ${d.low ? "text-rose-600" : "text-slate-800"}`} data-testid={`adv-free-${s}`}>{d.free}</p>
    <p className="text-xs text-slate-500">unused · {d.used} issued · {d.void} void · {d.total} this FY</p>
    <button type="button" onClick={onUpload} disabled={busy} className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-teal-600 px-3 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-50 disabled:opacity-50" data-testid={`adv-upload-${s}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload {s} list</button>
  </div>
);

const UploadResult = ({ r, onClose }) => (
  <div className={`rounded-xl border p-3 text-xs ${r.dup_count ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="adv-upload-result">
    <div className="flex items-center gap-2 font-semibold">
      <span>{r.service}: {r.added} new number(s) added</span>
      {r.dup_count > 0 && <span className="text-amber-800">· {r.dup_count} repeated — skipped</span>}
      {r.invalid_count > 0 && <span className="text-slate-500">· {r.invalid_count} cell(s) not a tracking number</span>}
      <button type="button" onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700" data-testid="adv-upload-result-close">Close</button>
    </div>
    {r.dup_count > 0 && (
      <div className="mt-2 max-h-40 overflow-y-auto rounded border border-amber-200 bg-white p-2">
        {r.duplicates.map((x) => <div key={x.number + x.where} className="flex gap-3"><span className="font-mono">{x.number}</span><span className="text-amber-800">{x.where}</span></div>)}
      </div>)}
  </div>
);

export default function AdvanceTrackingPanel() {
  const [sum, setSum] = useState(null);
  const [svc, setSvc] = useState("ITPS");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState(null);
  const inp = useRef(null);
  const upSvc = useRef("ITPS");
  const h = { headers: getAuthHeader() };
  const loadSum = () => axios.get(`${API}/advance-tracking/summary`, h).then((r) => setSum(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Could not load"));
  const loadRows = () => axios.get(`${API}/advance-tracking/list`, { ...h, params: { service: svc, status, q } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { loadSum(); }, []);
  useEffect(() => { loadRows(); }, [svc, status, q]);

  const upload = async (file) => {
    const s = upSvc.current;
    setBusy(s); setRes(null);
    const fd = new FormData(); fd.append("service", s); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/advance-tracking/upload`, fd, h);
      setRes(r.data);
      r.data.dup_count ? toast.warning(`${r.data.dup_count} number(s) already used this financial year — skipped`) : toast.success(`${r.data.added} ${s} numbers added`);
      setSvc(s); loadSum(); loadRows();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy("");
  };

  if (!sum) return <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  return (
    <div className="space-y-4" data-testid="advance-tracking-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-bold">Advance Tracking</h2>
        <span className="text-xs text-slate-500">FY {sum.fy} (Apr–Mar) · each number once per year · Hub orders get one per parcel by service · warns under {sum.low_stock}</span>
        <button type="button" onClick={() => { loadSum(); loadRows(); }} className="ml-auto rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Refresh" data-testid="adv-refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>
      <input ref={inp} type="file" accept=".xlsx,.xlsm,.csv,.txt" className="hidden" data-testid="adv-upload-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
      <div className="grid gap-3 md:grid-cols-3">
        {SERVICES.map((s) => <BalanceCard key={s} s={s} d={sum.services[s]} active={svc === s} busy={busy === s}
          onPick={() => setSvc(s)} onUpload={() => { upSvc.current = s; inp.current?.click(); }} />)}
      </div>
      {res && <UploadResult r={res} onClose={() => setRes(null)} />}
      <div className="rounded-xl border bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <b className="text-sm">{svc} numbers</b>
          {["", "free", "used", "void"].map((s) => (
            <button key={s || "all"} type="button" onClick={() => setStatus(s)} data-testid={`adv-filter-${s || "all"}`}
              className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${status === s ? "border-teal-600 bg-teal-600 text-white" : "text-slate-600"}`}>
              {{ "": "All", free: "Unused", used: "Issued", void: "Void" }[s]}</button>))}
          <div className="relative ml-auto w-64"><Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Number, customer or code" className="w-full rounded-lg border py-1.5 pl-8 pr-3 text-sm" data-testid="adv-search" /></div>
        </div>
        <div className="max-h-[480px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 text-left text-slate-500"><tr><th className="px-3 py-2">Number</th><th>Status</th><th>Customer</th><th>Code</th><th>Issued</th><th>Uploaded</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.number} className="border-t" data-testid={`adv-row-${r.number}`}>
                <td className="px-3 py-1.5 font-mono">{r.number}</td>
                <td><span className={`rounded px-1.5 py-0.5 font-semibold ${ST[r.status]}`}>{{ free: "Unused", used: "Issued", void: "Void" }[r.status]}</span>{r.void_reason && <span className="ml-1 text-slate-400">{r.void_reason}</span>}</td>
                <td>{r.customer_name || ""}</td><td>{r.code || ""}</td>
                <td className="text-slate-500">{r.assigned_at ? new Date(r.assigned_at).toLocaleString() : ""}</td>
                <td className="text-slate-500">{new Date(r.uploaded_at).toLocaleDateString()}</td>
              </tr>))}
              {!rows.length && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No numbers{q || status ? " match" : " yet — upload a list"}.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
